import DMFInsightIcon from '@/assets/icons/DMFInsightIcon';
import Card from '@/components/Card';
import ClickableTooltip from '@/components/ClickableTooltip';
import { AppSelect } from '@/components/input/AppSelect';
import AutocompleteSelect from '@/components/input/AutocompleteSelect';
import { AppInput } from '@/components/input/Input';
import TitleAndText from '@/components/TitleAndText';
import Tooltip from '@/components/Tooltip';
import enums from '@/enums/enums';
import { useMedia } from '@/hooks/useMedia';
import { PROFESSIONEL_PRODUCT_CODE, useMutationStripePortalUrl } from '@/hooks/useStripe';
import { getVehicleBrands } from '@/services/hub';
import { getTaxInsightEntires, getTaxInsightStatitics } from '@/services/taxInsights';
import { TaxInsightItem, TaxInsightsEntiresParams } from '@/types/taxInsightTypes';
import { formatDate, formatNumber, formatPercent, formatPrice, getDayDifference } from '@/utils/string';
import { Button, Disclosure, DisclosureButton, DisclosurePanel, Field, Fieldset, Label } from '@headlessui/react';
import { ArrowDownIcon, ArrowsUpDownIcon, ChevronDownIcon } from '@heroicons/react/16/solid';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { ChartBarIcon, CheckBadgeIcon, InformationCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, useLoaderData, useRouter } from '@tanstack/react-router';
import clsx from 'clsx';
import { t } from 'i18next';
import { Fragment, useState } from 'react';
import { LoaderIcon } from 'react-hot-toast';
import z from 'zod';

const DEFAULT_PAGE_SIZE = 20;
const schema = z.object({
    sort: z.enum(['published', '-published', 'id', '-id']).default('-published').optional(),
    filters: z.object({
        type: z.string().optional(),
        brand: z.number().or(z.string()).optional(),
        q: z.string().optional(),
        year: z.number().optional(),
        published: z.number().optional(),
    }).optional()
})

export const Route = createFileRoute('/_protected/_insights/insights')({
    component: RouteComponent,
    validateSearch: schema,
    head: () => ({
        meta: [{ title: `DMF Insight` }],
    }),
})

const years: number[] = []
const currentYear = new Date().getFullYear()
for (let year = currentYear; year >= 1962; year--) {
    years.push(year)
}
const evalationPeriods = [
    { name: "2026", value: 2026 },
    { name: "2025-2026", value: 2025 },
    { name: "2024-2026", value: 2024 },
    { name: "2023-2026", value: 2023 },
    { name: "2022-2026", value: 2022 },
    { name: "2021-2026", value: 2021 },
    { name: "Alle historiske", value: -1 },
]
function printModelVariant(item: TaxInsightItem) {
    if (!item.modelDesignation && !item.modelPeriod) {
        return item.model + " " + item.variant
    }
    // Assume we have DAT data, and as such the model prop is a sales description
    return item.model ?? "";
}

function RouteComponent() {
    const { user } = useLoaderData({ from: "/_protected" })
    const { mutate } = useMutationStripePortalUrl("swap_" + PROFESSIONEL_PRODUCT_CODE)
    const search = Route.useSearch()
    const navigate = Route.useNavigate()
    const { data: vehicleBrands, isLoading: isLoadingBrands } = useQuery({
        queryKey: ['vehicleBrands'],
        queryFn: async () => getVehicleBrands(),
        refetchOnWindowFocus: false,
        select(data) {
            return [{ title: "Alle", id: -1 }, ...(data?.data?.data ?? [])]
        },
    })
    const [publishingPeriodYear, setPublishingPeriodYear] = useState(search.filters?.published)
    const [type, setType] = useState(search.filters?.type)
    const [brand, setBrand] = useState<number | string | undefined>(search.filters?.brand ?? -1)
    const [query, setQuery] = useState(search.filters?.q ?? "")
    const [year, setYear] = useState(search.filters?.year ?? -1)

    const {
        data,
        isLoading,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ["taxInsight", "entries", JSON.stringify(search)],
        queryFn: async ({ pageParam, signal }) => {
            const params: TaxInsightsEntiresParams = {
                page: pageParam,
                pageSize: DEFAULT_PAGE_SIZE,
                ...search
            };
            return (await getTaxInsightEntires(params, signal)).data
        },
        getNextPageParam: (lastPage) => {
            if (lastPage.links.next) {
                return lastPage.meta.current_page + 1;
            }
            return undefined;
        },
        initialPageParam: 1,
        staleTime: 300_000, // 5 minutes, 1000ms x 60s x 5m
        refetchOnWindowFocus: true, // when query is stale, refetch on window focus
    })
    const totalHits = data?.pages?.[(data?.pages?.length ?? 1) - 1]?.meta?.total ?? 0;
    const showingTotal = data?.pages?.reduce((prev, curr) => (prev ?? 0) + (curr?.data?.length ?? 0), 0) ?? 0
    const itemsLeft = totalHits - showingTotal;

    function applyFilters() {
        navigate({
            search: {
                ...search,
                filters: {
                    type: type || undefined,
                    brand: brand === -1 ? undefined : brand,
                    q: query || undefined,
                    year: year === -1 ? undefined : year,
                    published: publishingPeriodYear === -1 ? undefined : publishingPeriodYear
                }
            }
        })
    }
    function clearFilters() {
        setType("")
        setBrand(-1)
        setQuery("")
        setYear(-1)
        navigate({
            search: {
                ...search,
                filters: undefined,
            }
        })
    }
    function filterForVehicle(item: TaxInsightItem) {
        const brand = vehicleBrands?.find(b => b.title === item.brand)
        setType(item?.type?.value ?? "1")
        setBrand(brand?.id ?? -1)
        const q = printModelVariant(item);
        setQuery(q)
        setYear(item.year ?? new Date().getFullYear())
        setPublishingPeriodYear(parseInt(item.createdAt?.slice(0, 4) ?? new Date().getFullYear().toString()))
        navigate({
            search: {
                ...search,
                filters: {
                    brand: brand?.id,
                    published: parseInt(item.createdAt?.slice(0, 4) ?? new Date().getFullYear().toString()),
                    type: item?.type?.value || undefined,
                    q,
                    year: item?.year || undefined
                }
            }
        })
    }
    function setSort(newSort: TaxInsightsEntiresParams['sort']) {

        let sort = search.sort;
        if (newSort === 'published') {
            if (sort === '-published') {
                sort = 'published';
            } else if (sort === 'published') {
                sort = undefined;
            } else {
                sort = '-published';
            }
        }
        if (newSort === 'id') {
            if (sort === '-id') {
                sort = 'id';
            } else if (sort === 'id') {
                sort = undefined;
            } else {
                sort = '-id';
            }
        }

        navigate({
            search: {
                ...search,
                sort
            }
        })
    }


    return (
        <div className='p-4 relative'>
            <div className='mb-4'>
                <div className='flex items-center gap-2 mb-1'>
                    <span className='text-primary'><DMFInsightIcon /> </span>
                    <h2 className='text-lg font-medium text-dark-blue'>DMF Insights</h2>
                </div>
                <p className='text-base'>Se hvad DMF tidligere har vurderet tilsvarende køretøjer til.</p>
            </div>
            {user.subscription.details?.package?.productCode !== PROFESSIONEL_PRODUCT_CODE && user?.details?.taxInsightTrialEndsAt && (
                <div className='px-3 py-2 mb-4 bg-blue-100 border border-primary rounded-sm flex max-sm:flex-col justify-between sm:items-center gap-4'>
                    <div className="flex gap-2 items-center">
                        <SparklesIcon className='size-4 shrink-0 text-primary' />
                        <p className='text-sm text-gray-800'>Du har {getDayDifference(user.details.taxInsightTrialEndsAt)} dage tilbage af din prøveperiode på DMF Insights</p>
                    </div>
                    <Button onClick={() => mutate()} className="link whitespace-nowrap text-sm">Få permanent adgang</Button>
                </div>
            )}
            <Card>
                <div className='flex justify-between max-sm:flex-col mb-4'>
                    <TitleAndText title='Find køretøj'
                        text="Filtrér på køretøjet for at se DMF's tidligere vurderinger."
                    />
                    <Field className={clsx("flex md:items-center max-md:flex-col md:gap-2 gap-1")}>
                        <Label className="text-xs text-text-secondary">Vurderingsperiode</Label>
                        <AppSelect value={publishingPeriodYear} onChange={(e) => setPublishingPeriodYear(parseInt(e.target.value))} options={evalationPeriods}
                        />
                    </Field>
                </div>
                <Fieldset className="flex gap-4 md:items-end justify-between max-md:flex-col"
                    onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                >
                    <div className="flex md:gap-4 gap-2 md:items-center max-lg:flex-wrap max-md:flex-col">
                        <Field className={"flex flex-col gap-1"}>
                            <Label className={"text-xs text-text-secondary"}>Type</Label>
                            <AppSelect value={type} onChange={(change) => setType(change.target.value)} options={
                                enums.vehicle.type
                                    .filter(item => item.value !== "127")
                                    .concat({ value: "", name: "Alle" })
                            } />
                        </Field>
                        <Field className={"flex flex-col gap-1"}>
                            <Label className={"text-xs text-text-secondary"}>Mærke</Label>
                            <AutocompleteSelect loading={isLoadingBrands} className='md:max-w-48'
                                value={brand ?? -1}
                                onChange={setBrand}
                                options={vehicleBrands || []} />
                        </Field>
                        <Field className={"flex flex-col gap-1"}>
                            <Label className={"text-xs text-text-secondary"}>Model / variant</Label>
                            <AppInput className={""} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={"iX3 M Sport..."} />
                        </Field>
                        <Field className={"flex flex-col gap-1"}>
                            <Label className={"text-xs text-text-secondary"}>Årgang</Label>
                            <AppSelect value={year} onChange={(e) => setYear(parseInt(e.target.value))}
                                className={""}
                                options={
                                    [{ value: -1, name: "" }, ...years.map(year => ({ value: year, name: year }))]
                                }
                            />
                        </Field>
                    </div>

                    <div className='flex gap-2 max-md:self-end'>
                        <Button onClick={applyFilters} className={"btn btn-primary"}>
                            {isFetching ? (
                                <LoaderIcon className='size-4 border-white/50 border-r-white' />
                            ) : (
                                <MagnifyingGlassIcon className='size-5' />
                            )}
                            <span>{t("search")}</span>
                        </Button>
                        <Button onClick={clearFilters} className={"btn btn-gray"}>
                            <XMarkIcon className='size-5' />
                            <span>Ryd filtre</span>
                        </Button>
                    </div>

                </Fieldset>
            </Card>

            <div className="mt-6">
                <BasicAnalyticsCard />
            </div>

            <Card className='mt-6 relative min-h-28 p-[0px!important]'>
                {isLoading && (
                    <LoaderIcon className='size-6 top-14 absolute left-[50%] -translate-x-1/2 border-r-primary-active' />
                )}
                <div className='overflow-auto w-full'>
                    <table className="tax-dashboard-table min-w-full divide-gray-300">
                        <thead className="bg-gray-100 z-10">
                            <tr>
                                <th scope="col" className="dmf-table-cell dmf-insights-table-cell first">
                                    <div className='flex gap-1 items-center hover:text-gray-700 cursor-pointer'
                                        onClick={() => setSort('published')}
                                    >
                                        <span>
                                            Vurderet
                                        </span>
                                        {(search.sort === 'published' || search.sort === '-published') ? (
                                            <ArrowDownIcon className={clsx(
                                                'size-3 shrink-0',
                                                search.sort === 'published' && 'rotate-180',
                                                search.sort === '-published' && 'rotate-0',
                                            )} />
                                        ) : (
                                            <ArrowsUpDownIcon className={clsx(
                                                'size-3 shrink-0',
                                            )} />
                                        )}
                                    </div>
                                </th>
                                <th scope="col" className="dmf-table-cell dmf-insights-table-cell">
                                    {t("brand")}
                                </th>
                                <th scope="col" className="dmf-table-cell dmf-insights-table-cell">
                                    {t("model")} / {t("variant")}
                                </th>
                                <th scope="col" className="dmf-table-cell dmf-insights-table-cell">
                                    {t("year")}
                                </th>
                                <th scope="col" className="dmf-table-cell dmf-insights-table-cell">
                                    {t("mileage")}
                                </th>
                                <th scope="col" className="dmf-table-cell dmf-insights-table-cell ">
                                    {t("newPrice")}
                                </th>
                                <th scope="col" className="dmf-table-cell dmf-insights-table-cell">
                                    {t("tradePrice")}
                                </th>
                                <th scope="col" className="dmf-table-cell dmf-insights-table-cell">
                                    Værditab %
                                </th>
                                <th scope="col" className="dmf-table-cell dmf-insights-table-cell text-right pr-4">
                                    Afgift
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white w-full">
                            {data?.pages?.map((page) => (
                                <Fragment key={page.links.next}>
                                    {page.data.map((item, index) => (
                                        <tr className={clsx("group")} key={index}>
                                            <td className="dmf-table-cell first max-sm:text-xs">
                                                <span className="bg-gray-100 text-xs px-1 py-0.5 rounded-sm">
                                                    {formatDate(item.createdAt)}
                                                </span>
                                            </td>
                                            <td className="dmf-table-cell text-dark-blue">
                                                {item.brand}
                                            </td>
                                            <td className="dmf-table-cell max-w-64" title={printModelVariant(item)}>
                                                <div className='relative max-w-full w-fit truncate pr-6'>
                                                    <span>
                                                        {printModelVariant(item)}
                                                    </span>
                                                    <Button className='absolute right-1 top-0.5 transition-opacity opacity-0 group-hover:opacity-100 hover:text-dark-blue'
                                                        onClick={() => filterForVehicle(item)}
                                                    >
                                                        <MagnifyingGlassIcon className='size-4' />
                                                    </Button>
                                                </div>
                                            </td>
                                            <td className="dmf-table-cell">
                                                {item.year}
                                            </td>
                                            <td className="dmf-table-cell">
                                                {formatNumber(item.mileage)}
                                            </td>
                                            <td className="dmf-table-cell">
                                                {formatPrice(item.newPrice, true)}
                                            </td>
                                            <td className="dmf-table-cell">
                                                {formatPrice(item.tradePrice, true)}
                                            </td>
                                            <td className="dmf-table-cell">
                                                {formatPercent(item.valueLoss)}
                                            </td>
                                            <td className="dmf-table-cell font-medium text-dark-blue">
                                                <div className='flex gap-2 items-center justify-end text-right'>
                                                    <span>{formatPrice(item.registrationFee, true)}</span>
                                                    {item.isConfirmed && (
                                                        <Tooltip body="Verificeret af Motorstyrelsen"
                                                            btn={
                                                                <span className='shrink-0'>
                                                                    <CheckBadgeIcon className='size-4 text-success' />
                                                                </span>
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className='flex w-full justify-center pb-4 mt-6'>
                    <div className='flex flex-col items-center gap-2'>
                        <Button onClick={() => fetchNextPage()} disabled={isFetchingNextPage || !hasNextPage} className="btn btn-gray">
                            {isFetchingNextPage && (<LoaderIcon />)}
                            <span>Vis {itemsLeft < DEFAULT_PAGE_SIZE ? itemsLeft : DEFAULT_PAGE_SIZE} flere</span>
                        </Button>
                        <span className='text-sm'>
                            Viser {formatNumber(showingTotal)}
                            {' '}af {formatNumber(totalHits)} vurderinger
                        </span>
                    </div>
                </div>
            </Card>

            <div className="mt-6">
                <ExpandedAnalyticsCard />
            </div>

            <StickyInfo />
        </div>
    )
}
function StickyInfo() {
    const isSmallViewport = useMedia('(max-width: 640px)')
    const router = useRouter()
    function goToCreateTaxCase() {
        router.navigate({ from: "/insights", to: "/tax/create" })
    }

    return (
        <div className='sticky bottom-0 z-30 pb-3 mt-6 bg-gradient-to-t to-transparent from-gray-100 via-gray-100'>
            {isSmallViewport ? (
                <Disclosure as={"div"} className='rounded-sm border bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.06)]'>
                    <DisclosureButton className={"group w-full px-4 py-3 flex gap-2 justify-between items-center"}>
                        <div className='flex items-center gap-2'>
                            <InformationCircleIcon className='size-5 flex-shrink-0 text-primary' />
                            <h3 className='text-base text-dark-blue'>Har du brug for et konkret tal?</h3>
                        </div>
                        <ChevronDownIcon className='size-4 transition-transform group-data-[open]:rotate-180' />
                    </DisclosureButton>
                    <DisclosurePanel className={"p-4 pt-0"}>
                        <p className='text-[12px] leading-[18px] tracking-[-0.16px] mb-4'>DMF Insights giver en markedsbaseret indikation baseret på historiske vurderinger. Med TAX får du en konkret forhåndsvurdering på det enkelte køretøj, som er bindende i 30 dage.</p>
                        <Button onClick={goToCreateTaxCase} className={"btn btn-primary max-sm:w-full"}>Opret værdifastsættelse</Button>
                    </DisclosurePanel>
                </Disclosure>
            ) : (

                <div className='p-4 rounded-sm border bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.06)] flex max-sm:flex-col justify-between items-center gap-4'>
                    <div className='flex gap-2'>
                        <InformationCircleIcon className='size-5 flex-shrink-0 text-primary' />
                        <div className='max-w-4xl'>
                            <h3 className='text-base text-dark-blue mb-1'>Har du brug for et konkret tal?</h3>
                            <p className='text-[12px] leading-[18px] tracking-[-0.16px]'>DMF Insights giver en markedsbaseret indikation baseret på historiske vurderinger. Med TAX får du en konkret forhåndsvurdering på det enkelte køretøj, som er bindende i 30 dage.</p>
                        </div>
                    </div>
                    <Button onClick={goToCreateTaxCase} className={"btn btn-primary max-sm:w-full"}>Opret værdifastsættelse</Button>
                </div>
            )}
        </div>

    )
}

function BasicAnalyticsCard() {
    const search = Route.useSearch()
    const { data: statistics } = useQuery({
        queryKey: ["taxInsight", "statistics", JSON.stringify(search)],
        queryFn: () => getTaxInsightStatitics(search),
        select(data) {
            return data?.data?.data
        },
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    })

    const {
        averageNewPrice,
        averageRegistrationFee,
        averageTradePrice,
        entryCount,
    } = statistics?.basic || {}
    if (!statistics) return null;
    return (
        <Card>
            <div className='text-xs uppercase tracking-wide flex items-center gap-2'>
                <span>Gennemsnitlig afgift på vurderingerne</span>
                <ClickableTooltip btn={<span><InformationCircleIcon className='size-4' /></span>}
                    body='Gennemsnittet af de faktiske registreringsafgifter i de viste vurderinger. Det er ikke et estimat på et konkret køretøj og udgør ikke et bindende tilbud.'
                />
            </div>
            <div className='mt-4 mb-2 text-3xl font-semibold text-dark-blue'>
                {formatPrice(averageRegistrationFee, true)}
            </div>
            <div className="text-xs">
                <p>{formatNumber(entryCount)} vurderinger {search.filters?.published ? `i perioden ${search.filters?.published} - ${new Date().getFullYear()}` : "i alt"}</p>
                <p className='text-text-secondary'>Gns. nypris {formatPrice(averageNewPrice, true)} · Gns. handelspris {formatPrice(averageTradePrice, true)}.</p>
            </div>
        </Card>
    )
}

function ExpandedAnalyticsCard() {
    const search = Route.useSearch()
    const { data: statistics } = useQuery({
        queryKey: ["taxInsight", "statistics", JSON.stringify(search)],
        queryFn: () => getTaxInsightStatitics(search),
        select(data) {
            return data?.data?.data
        },
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    })

    const {
        maxRegistrationFee, medianRegistrationFee, minRegistrationFee
    } = statistics?.basic || {}

    if (!statistics) return null;
    return (
        <Disclosure as="div" className="w-full bg-white border rounded-sm">
            <DisclosureButton className="group w-full px-4 py-3 text-left flex items-center justify-between">
                <div className='flex items-center gap-2'>
                    <ChartBarIcon className='size-5' />
                    <span className='text-base text-dark-blue'>Udvidet analyse</span>
                    <span className='text-sm text-text-secondary'>Årgang, kilometerstand og spredning</span>
                </div>
                <ChevronDownIcon className='size-4 transition-transform group-data-[open]:rotate-180' />
            </DisclosureButton>
            <div className="overflow-hidden">
                <DisclosurePanel transition
                    className={clsx(
                        "origin-top transition duration-200 ease-out data-[closed]:-translate-y-6 data-[closed]:opacity-0",
                        "border-t p-4"
                    )}
                >
                    <p className='text-xs text-text-secondarya mb-4'>
                        Oprindelig registreringsafgift som beregnet på vurderingstidspunktet · indeværende år.
                    </p>
                    <div className='flex items-center gap-4 mb-4'>
                        <div className='flex items-center gap-2'>
                            <span className='text-xs uppercase tracking-wide text-text-secondary'>Laveste</span>
                            <span className="text-base text-dark-blue font-medium">{formatPrice(minRegistrationFee, true)}</span>
                        </div>

                        <div className='border-r h-8'></div>

                        <div className='flex items-center gap-2'>
                            <span className='text-xs uppercase tracking-wide text-text-secondary'>Median</span>
                            <span className="text-base text-dark-blue font-medium">{formatPrice(medianRegistrationFee, true)}</span>
                        </div>

                        <div className='border-r h-8'></div>

                        <div className='flex items-center gap-2'>
                            <span className='text-xs uppercase tracking-wide text-text-secondary'>Højeste</span>
                            <span className="text-base text-dark-blue font-medium">{formatPrice(maxRegistrationFee, true)}</span>
                        </div>
                    </div>
                    <div className='border p-4 bg-slate-50'>
                        <p className='text-base font-medium text-dark-blue'>Gennemsnitlig afgift efter årgang og kilometerstand</p>
                        <table className='mt-4 min-w-full divide-y divide-gray-300 text-right'>
                            <thead>
                                <tr className='text-xs text-text-secondary uppercase tracking-wide'>
                                    <th className='text-left font-normal p-1'>Årgang</th>
                                    <th className="font-normal p-1">0–50.000 km	</th>
                                    <th className="font-normal p-1">50.000–100.000 km	</th>
                                    <th className="font-normal p-1">100.000–150.000 km	</th>
                                    <th className="font-normal p-1">150.000+ km</th>
                                </tr>
                            </thead>
                            <tbody className='text-sm divide-y divide-gray-200 w-full'>
                                {Object.keys(statistics?.yearRangeAnalysis ?? {}).map((key) => {
                                    const item = statistics?.yearRangeAnalysis[key]
                                    return (
                                        <tr key={key} className='hover:bg-slate-100'>
                                            <td className='text-left p-1 font-medium text-dark-blue'>{item?.year}</td>
                                            <td className='p-1'>
                                                {item?.['0–50000'] == null ? "—" : formatPrice(item?.['0–50000'], true)}
                                            </td>
                                            <td className='p-1'>
                                                {item?.['50000–100000'] == null ? "—" : formatPrice(item?.['50000–100000'], true)}
                                            </td>
                                            <td className='p-1'>
                                                {item?.['100000–150000'] == null ? "—" : formatPrice(item?.['100000–150000'], true)}
                                            </td>
                                            <td className='p-1'>
                                                {item?.['150000+'] == null ? "—" : formatPrice(item?.['150000+'], true)}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </DisclosurePanel>
            </div>
        </Disclosure>

    )
}
